"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { type OrganizationsOutput, UserRoles } from "@/interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { MemberList } from "./components/MemberList";
import { OrganizationService } from "@/services/OrganizationService";
import type { UsersOutput } from "@/interfaces/users";
import { UsersService } from "@/services/UserService";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { addDays } from "date-fns";
import { InvitesService } from "@/services/InvitesService";
import { InvitedList } from "./components/InvitedList";
import type { InvitesOutput } from "@/interfaces/invites";
import { Separator } from "@/components/ui/separator";
import { localDate } from "@/util/dates";
import { ArrowLeft, Users } from "lucide-react";

export default function OrganizationMembers() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<OrganizationsOutput | null>(
    null
  );
  const [userMe, setUserMe] = useState<UsersOutput>();
  const [isLoading, setIsLoading] = useState(true);
  const [invites, setInvites] = useState<InvitesOutput[]>([]);
  const service = new OrganizationService();
  const userService = new UsersService();
  const invitesService = new InvitesService();

  const fetchOrganization = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [fetchedOrganization, fetchedUser] = await Promise.all([
        service.findById(id, { expand: "users" }),
        userService.getUserMe(),
      ]);

      const fetchedInvites = await invitesService.findByOrganization(
        fetchedOrganization.id
      );

      setOrganization(fetchedOrganization);
      setUserMe(fetchedUser);
      setInvites(fetchedInvites);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const handleAddMember = async (email: string) => {
    try {
      setIsLoading(true);
      const expiresAt = addDays(localDate(), 7);
      await invitesService.create({
        userEmail: email,
        organizationId: organization!.id,
        role: UserRoles.MEMBER,
        expiresAt,
      });
      await fetchOrganization();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      setIsLoading(true);
      await service.removeUser(id!, userId);
      await fetchOrganization();
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMemberRole = async (userId: string, newRole: UserRoles) => {
    try {
      setIsLoading(true);
      await service.updateUserRole(id!, userId, newRole);
      await fetchOrganization();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      setIsLoading(true);
      await invitesService.declineInvite(inviteId);
      await fetchOrganization();
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferOwnership = async (newOwnerId: string) => {
    try {
      setIsLoading(true);
      await service.transferOwnership(id!, newOwnerId);
      await fetchOrganization();
    } finally {
      setIsLoading(false);
    }
  };

  const userRole = () => {
    return organization?.users?.find(
      (member) => member.user.userId === userMe!.userId
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-center mb-4">
            Organização não encontrada
          </h1>
          <p className="text-center text-gray-600 mb-6">
            A organização que você está procurando não existe ou você não tem
            permissão para acessá-la.
          </p>
          <Button
            variant="default"
            onClick={() => navigate("/organizacoes")}
            className="w-full bg-[#EE7B11] hover:bg-[#d06a0a]"
          >
            Voltar para Organizações
          </Button>
        </div>
      </div>
    );
  }

  const userLoggedRole = userRole();

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#EE7B11] to-[#F9A826] rounded-t-lg p-6 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Gerenciar Membros</h1>
            <p className="text-white/80 mt-1">{organization.name}</p>
          </div>
          <div className="bg-white/20 p-3 rounded-full">
            <Users className="h-6 w-6 text-white" />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="bg-white rounded-b-lg shadow-md p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                Membros da Organização
              </h2>
              <div className="ml-3 bg-[#EE7B11]/10 text-[#EE7B11] text-sm font-medium px-2.5 py-0.5 rounded-full">
                {organization.users?.length || 0} membros
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/organizacoes/${id}`)}
              className="text-[#EE7B11] border-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
          </div>

          <MemberList
            userLoggedRole={userLoggedRole!.role}
            members={organization.users}
            onAddMember={handleAddMember}
            onRemoveMember={handleRemoveMember}
            onUpdateMemberRole={handleUpdateMemberRole}
            onTransferOwnership={handleTransferOwnership}
          />

          {invites?.length > 0 && (
            <>
              <Separator className="my-8" />
              <InvitedList
                isOwner={userLoggedRole!.role === UserRoles.OWNER}
                invitedUsers={invites}
                onCancelInvite={handleCancelInvite}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
